import React from "react";
import { Droppable, DroppableProvided, DroppableStateSnapshot } from "react-beautiful-dnd";
import styled from "styled-components";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { StyledButton } from "../../CommonUI/BuildlingBlocks/StyledButton";

import { PropertyEditorBoolean } from "../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorColor } from "../../CommonUI/PropertyEditing/PropertyEditorColor";

import { PropertyEditorInteger } from "../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorToggleButtons } from "../../CommonUI/PropertyEditing/PropertyEditorToggleButtons";
import { PropertyEditorSelect } from "../../CommonUI/PropertyEditing/PropertyEditorSelect";

import { FormControl } from "../Controls/FormControl";
import { ChildFullWidthDiv } from "../Controls/Web/CommonStyled";
import { BorderStyle, FormDesignConstants, FormMode } from "../Utilities/FormDesignConstants";
import { DefaultOffOverride } from "../Utilities/DefaultOffOverride";
import { XDesignWrapper } from "../Utilities/XDesignWrapper";
import { XRuntimeWrapper } from "../Utilities/XRuntimeWrapper";

import { XBaseContainer } from "./XBaseContainer";
import { XBaseStackContainer } from "./XBaseStackContainer";
import { IGridArea, IGridContainer, XGridEditLayer } from "./XGridEditLayer";
import { StackContainerColumn, StackContainerDiv } from "./XStackContainer";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { PropertyEditorCollapse } from "../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";

export class XGridRow extends FormControl {
    readonly __type: string = "Grid Row";
    height?: number;

    getDesignValidationIssues(): ValidationIssue[] {
        return [];
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return;
    }

    renderEditUI() {
        return (
            <PropertyEditorInteger
                editObject={this}
                propertyName="height"
                label="Height"
                onChange={this.onControlUpdate}
            />
        );
    }

    // this gets replaced
    onControlUpdate = () => {};
}

export class XGridArea extends XBaseStackContainer implements IGridArea {
    readonly __type: string = "Grid Container";
    row: number;
    column: number;

    width: number = 1;
    height: number = 1;

    container?: XGridContainer;
    oneControl: boolean = false;
    showBorder?: boolean;
    backgroundColorSettings: DefaultOffOverride = DefaultOffOverride.DEFAULT;
    innerMarginSettings: DefaultOffOverride = DefaultOffOverride.DEFAULT;
    formBorderSettings: DefaultOffOverride = DefaultOffOverride.DEFAULT;
    controlAlign: {
        top?: number;
        left?: number;
        width?: number;
        height?: number;
    } = {};

    constructor(row: number, column: number, width: number = 1, height: number = 1) {
        super();
        this.row = row;
        this.column = column;
        this.width = width;
        this.height = height;
    }

    // this gets replaced
    onControlUpdate = () => {};

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "row",
            "column",
            "width",
            "height",
            "oneControl",
            "showBorder",
            "backgroundColorSettings",
            "innerMarginSettings",
            "formBorderSettings",
            "controlAlign",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "row",
            "column",
            "width",
            "height",
            "oneControl",
            "showBorder",
            "backgroundColorSettings",
            "innerMarginSettings",
            "formBorderSettings",
            "controlAlign",
        );
    }

    add(control: FormControl, index?: number) {
        if (this.oneControl && this.controls.length === 1) {
            return;
        }

        // set control order
        if (index === undefined || index === null) {
            this.controls.push(control);
        } else {
            this.controls.splice(index, 0, control);
        }

        if (this.getFormContext()) control.setFormContext(this.getFormContext()!);
    }

    backgroundColor(): string | undefined | null {
        return (
            (this.backgroundColorSettings === DefaultOffOverride.Override && this.containerBackgroundColor) ||
            this.container?.defaultAreaBackgroundColor ||
            super.backgroundColor()
        );
    }

    hasOwnBorder(): boolean | undefined {
        return this.formBorderSettings === DefaultOffOverride.Override;
    }

    hasBorder(): boolean | undefined {
        return (
            this.hasOwnBorder() ||
            (this.formBorderSettings === DefaultOffOverride.DEFAULT &&
                this.getFormContext()?.form?.defaultShowContainerBorder)
        );
    }

    borderColor(): string | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderColor) ||
            this.container?.defaultAreaBorderColor ||
            super.borderColor()
        );
    }

    borderStyle(): BorderStyle | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderStyle) ||
            this.container?.defaultAreaBorderStyle ||
            super.borderStyle()
        );
    }

    borderWidth(): number | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderWidth) ||
            this.container?.defaultAreaBorderWidth ||
            super.borderWidth()
        );
    }

    borderRadius(): number | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderRadius) ||
            this.container?.defaultAreaBorderRadius ||
            super.borderRadius()
        );
    }

    getInnerMargin(): number | undefined {
        return (
            (this.innerMarginSettings === DefaultOffOverride.Override && this.innerMargin) ||
            this.container?.defaultAreaInnerMargin
        );
    }

    renderEditUI() {
        return (
            <>
                <PropertyEditorInteger
                    editObject={this}
                    propertyName="row"
                    label="Row Index"
                    min={0}
                    max={this.container?.rowsLength ? this.container?.rowsLength - this.height : undefined}
                    hint={`max value: ${
                        this.container?.rowsLength ? this.container?.rowsLength - this.height : undefined
                    }`}
                    onChange={this.onControlUpdate}
                />
                <PropertyEditorInteger
                    editObject={this}
                    propertyName="column"
                    label="Column Index"
                    min={0}
                    max={(this.container?.columnsLength || 12) - this.width}
                    hint={`max value: ${(this.container?.columnsLength || 12) - this.width}`}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorInteger
                    editObject={this}
                    propertyName="width"
                    label="Width"
                    min={1}
                    max={(this.container?.columnsLength || 12) - this.column}
                    hint={`max value: ${(this.container?.columnsLength || 12) - this.column}`}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorInteger
                    editObject={this}
                    propertyName="height"
                    label="Height"
                    max={this.container?.rowsLength ? this.container?.rowsLength - this.row : undefined}
                    hint={`max value: ${
                        this.container?.rowsLength ? this.container?.rowsLength - this.row : undefined
                    }`}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorToggleButtons
                    editObject={this}
                    label="Border"
                    propertyName="formBorderSettings"
                    options={[DefaultOffOverride.DEFAULT, DefaultOffOverride.Off, DefaultOffOverride.Override]}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() => this.formBorderSettings === DefaultOffOverride.Override}
                    observeProperties={["formBorderSettings"]}
                >
                    <ChildFullWidthDiv>
                        <PropertyEditorInteger
                            editObject={this}
                            label="Border Width"
                            propertyName="containerBorderWidth"
                            parentDefaultValue={this.getFormContext()?.form.defaultContainerBorderWidth}
                            onChange={this.onControlUpdate}
                        />
                        <PropertyEditorInteger
                            editObject={this}
                            label="Border Radius"
                            propertyName="containerBorderRadius"
                            parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderRadius}
                            onChange={this.onControlUpdate}
                        />

                        <PropertyEditorColor
                            editObject={this}
                            label="Border Color"
                            propertyName="containerBorderColor"
                            parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderColor}
                            onChange={this.onControlUpdate}
                        />
                        <PropertyEditorSelect
                            editObject={this}
                            label="Border Style"
                            propertyName="containerBorderStyle"
                            options={FormDesignConstants.BORDER_STYLES}
                            parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderStyle}
                            onChange={this.onControlUpdate}
                        />
                    </ChildFullWidthDiv>
                </PropertyEditorCollapse>

                <PropertyEditorToggleButtons
                    editObject={this}
                    propertyName="innerMarginSettings"
                    label="Inner Margin"
                    options={[DefaultOffOverride.DEFAULT, DefaultOffOverride.Off, DefaultOffOverride.Override]}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() => this.innerMarginSettings === DefaultOffOverride.Override}
                    observeProperties={["innerMarginSettings"]}
                >
                    <PropertyEditorInteger
                        editObject={this}
                        propertyName="innerMargin"
                        label="Area Inner Margin"
                        onChange={this.onControlUpdate}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorToggleButtons
                    editObject={this}
                    propertyName="backgroundColorSettings"
                    label="Background Color"
                    options={[DefaultOffOverride.DEFAULT, DefaultOffOverride.Off, DefaultOffOverride.Override]}
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() => this.backgroundColorSettings === DefaultOffOverride.Override}
                    observeProperties={["backgroundColorSettings"]}
                >
                    <PropertyEditorColor
                        editObject={this}
                        propertyName="containerBackgroundColor"
                        label="Content Background Color"
                        onChange={this.onControlUpdate}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorBoolean
                    editObject={this}
                    propertyName="oneControl"
                    label="Single Control?"
                    onChange={this.onControlUpdate}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"oneControl"}
                >
                    <ChildFullWidthDiv>
                        <PropertyEditorInteger
                            editObject={this.controlAlign}
                            propertyName="top"
                            label="Top"
                            onChange={this.onControlUpdate}
                        />
                        <PropertyEditorInteger
                            editObject={this.controlAlign}
                            propertyName="Left"
                            label="Left"
                            onChange={this.onControlUpdate}
                        />
                        <PropertyEditorInteger
                            editObject={this.controlAlign}
                            propertyName="Right"
                            label="right"
                            onChange={this.onControlUpdate}
                        />
                        <PropertyEditorInteger
                            editObject={this.controlAlign}
                            propertyName="bottom"
                            label="bottom"
                            onChange={this.onControlUpdate}
                        />
                    </ChildFullWidthDiv>
                </PropertyEditorCollapse>
            </>
        );
    }

    getDesignValidationIssues(): ValidationIssue[] {
        return [];
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return [];
    }

    onSelect = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        let mode = this.getFormContext()?.mode ?? FormMode.Runtime;
        if (mode === FormMode.LayoutDesign) {
            this.selectInDesigner();
        }
    };

    render() {
        let mode = this.getFormContext()?.mode ?? FormMode.Runtime;
        let controls = this.getControls();

        if (mode === FormMode.FormDesign) {
            return (
                <GridCellRender
                    padding={this.getInnerMargin()}
                    borderColor={this.borderColor()}
                    borderStyle={this.borderStyle()}
                    borderWidth={this.borderWidth()}
                    borderRadius={this.borderRadius()}
                    backgroundColor={this.backgroundColor()}
                    columnSpan={this.width}
                    columnStart={this.column}
                    rowSpan={this.height}
                    rowStart={this.row}
                    data-testid={`XGridCell-${this.row}-${this.column}`}
                >
                    <Droppable
                        droppableId={this.id}
                        isDropDisabled={this.oneControl && controls.length >= 1}
                    >
                        {(provided: DroppableProvided, snapShot: DroppableStateSnapshot) => {
                            return (
                                <StackContainerDiv
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                        border: snapShot.isDraggingOver
                                            ? `${FormDesignConstants.DROPPABLE_BORDER_STYLE} ${FormDesignConstants.DROPPABLE_BORDER_WIDTH}px ${FormDesignConstants.DROPPABLE_BORDER_COLOR}`
                                            : "none",
                                        borderRadius: snapShot.isDraggingOver
                                            ? FormDesignConstants.DROPPABLE_BORDER_RADIUS
                                            : 0,
                                    }}
                                >
                                    {this.getControls().map((control, index) => {
                                        return (
                                            <StackContainerColumn
                                                colGap={
                                                    this.interControlSpacing ||
                                                    this.getFormContext()?.form?.defaultInterControlSpacing
                                                }
                                                style={{
                                                    border: snapShot.isDraggingOver
                                                        ? `${FormDesignConstants.DROPPABLE_BORDER_STYLE} ${FormDesignConstants.DROPPABLE_BORDER_WIDTH}px ${FormDesignConstants.DROPPABLE_BORDER_COLOR}`
                                                        : "none",
                                                    borderRadius: snapShot.isDraggingOver
                                                        ? FormDesignConstants.DROPPABLE_BORDER_RADIUS
                                                        : 0,
                                                }}
                                                key={index}
                                                data-testid={control.id}
                                            >
                                                <XDesignWrapper
                                                    key={control.id}
                                                    id={control.id}
                                                    index={index}
                                                    innerComponent={control}
                                                    editContext={this.getFormContext()!}
                                                >
                                                    {control.render()}
                                                </XDesignWrapper>
                                            </StackContainerColumn>
                                        );
                                    })}
                                    {provided.placeholder}
                                </StackContainerDiv>
                            );
                        }}
                    </Droppable>
                    {this.getControls().length === 0 && this.renderEmptyControlDiv()}
                </GridCellRender>
            );
        }

        if (controls.length >= 1 && this.oneControl) {
            let control = controls[0];
            return (
                <GridCellRender
                    padding={0}
                    borderColor={this.borderColor()}
                    borderStyle={this.borderStyle()}
                    borderWidth={this.borderWidth()}
                    borderRadius={this.borderRadius()}
                    backgroundColor={this.backgroundColor()}
                    columnStart={this.column}
                    columnSpan={this.width}
                    rowStart={this.row}
                    rowSpan={this.height}
                    onClick={this.onSelect}
                    data-testid={`XGridCell-${this.row}-${this.column}`}
                >
                    <OneControlRender
                        position={this.controlAlign}
                        data-testid={control.id}
                    >
                        <StackContainerColumn
                            colGap={this.interControlSpacing || this.getFormContext()?.form?.defaultInterControlSpacing}
                        >
                            {/* <XDesignWrapper
                                key={control.id}
                                id={control.id}
                                index={0}
                                innerComponent={control}
                                editContext={this.getFormContext()!}
                            > */}
                            <XRuntimeWrapper
                                innerComponent={control}
                                context={this.getFormContext()!}
                            />
                            {/* </XDesignWrapper> */}
                        </StackContainerColumn>
                    </OneControlRender>
                </GridCellRender>
            );
        }

        return (
            <GridCellRender
                padding={this.getInnerMargin()}
                borderColor={this.borderColor()}
                borderStyle={this.borderStyle()}
                borderWidth={this.borderWidth()}
                borderRadius={this.borderRadius()}
                backgroundColor={this.backgroundColor()}
                columnStart={this.column}
                columnSpan={this.width}
                rowStart={this.row}
                rowSpan={this.height}
                onClick={this.onSelect}
                data-testid={`XGridCell-${this.row}-${this.column}`}
            >
                {controls.map((control, index) => {
                    return (
                        <StackContainerColumn
                            colGap={this.interControlSpacing || this.getFormContext()?.form?.defaultInterControlSpacing}
                            key={index}
                            data-testid={control.id}
                        >
                            {/* <XDesignWrapper
                                key={control.id}
                                id={control.id}
                                index={index}
                                innerComponent={control}
                                editContext={this.getFormContext()!}
                            > */}
                            <XRuntimeWrapper
                                innerComponent={control}
                                context={this.getFormContext()!}
                            />
                            {/* </XDesignWrapper> */}
                        </StackContainerColumn>
                    );
                })}
                {this.getControls().length === 0 && mode !== FormMode.LayoutDesign && this.renderEmptyControlDiv()}
            </GridCellRender>
        );
    }
}

export class XGridContainer extends XBaseContainer implements IGridContainer {
    readonly __type: string = "Grid Container";
    areas: XGridArea[] = [];

    rows: XGridRow[] = [new XGridRow()];

    // can not be less than 1
    rowsLength: number = 1;

    columnsLength: number = 12;

    innerMargin?: number;
    gapBetweenAreas: number = 10;

    // area
    defaultAreaInnerMargin?: number = 10;
    defaultShowAreaBorder?: boolean = false;
    defaultAreaBorderColor?: string = UIStyleConstants.GRAY_LIGHT_COLOR;
    defaultAreaBorderRadius?: number = 10;
    defaultAreaBorderWidth?: number = 2;
    defaultAreaBorderStyle?: BorderStyle;

    defaultAreaBackgroundColor?: string | null = UIStyleConstants.WHITE_COLOR;

    private showEditUIDialog: boolean = false;

    getDesignValidationIssues(): ValidationIssue[] {
        let issues = super.getDesignValidationIssues();

        if (this.areas.length === 0) {
            issues.push(new ValidationIssue("There is no area."));
        } else {
            if (!this.areas.find((area) => area.row === 0 && area.column === 0)) {
                issues.push(new ValidationIssue("should have one area contains [1, 1]", this.id));
            }

            if (
                this.rowsLength > 1 &&
                !this.areas.find((area) => area.row === this.rowsLength - 1 && area.column === 0)
            ) {
                issues.push(new ValidationIssue(`should have one area contains [${this.rowsLength}, 1]`, this.id));
            }

            if (!this.areas.find((area) => area.row === 0 && area.column === 11)) {
                issues.push(new ValidationIssue(`should have one area contains [1, 12]`, this.id));
            }

            if (
                this.rowsLength > 1 &&
                !this.areas.find((area) => area.row === this.rowsLength - 1 && area.column === 11)
            ) {
                issues.push(new ValidationIssue(`should have one area contains [${this.rowsLength}, 12]`, this.id));
            }
        }
        return issues;
    }

    remove(control: FormControl) {
        if (control instanceof XGridArea) {
            this.removeArea(control);
        }
    }

    find(id: string): FormControl | null {
        for (let row of this.rows) {
            if (row.id === id) return row;
        }

        for (let cont of this.areas) {
            if (cont.id === id) return cont;

            for (let c of cont.getControls()) {
                if (c.id === id) {
                    return c;
                }
            }
        }

        return null;
    }

    // eslint-disable-next-line
    setOrder(id: string, order: number) {}

    add(control: FormControl, index?: number) {
        if (control instanceof XGridArea) {
            this.addArea(control, index);
        } else if (control instanceof XGridRow) {
            this.addRow(control, index);
        }
    }

    getControls() {
        let controls: FormControl[] = [...this.areas, ...this.rows];

        for (let a of this.areas) {
            controls = [...controls, ...a.getControls()];
        }
        return controls;
    }

    renderEditUI() {
        return (
            <>
                <StyledButton
                    variant="contained"
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        this.showEditUIDialog = true;
                        StateManager.changed(this);
                    }}
                >
                    Open Edit UI
                </StyledButton>
                <ObserveState
                    listenTo={this}
                    control={() => {
                        if (this.showEditUIDialog) {
                            return (
                                <XGridEditLayer
                                    grid={this}
                                    updateCallback={() => {
                                        this.showEditUIDialog = false;
                                        StateManager.changed(this);
                                    }}
                                />
                            );
                        }

                        return <></>;
                    }}
                />
            </>
        );
    }

    @AutoBind
    addArea(area: XGridArea, index?: number) {
        area.container = this;

        if (this.getFormContext()) {
            area.setFormContext(this.getFormContext());
        }
        if (index !== undefined) {
            this.areas.splice(index, 1, area);
        } else {
            this.areas.push(area);
        }
    }

    @AutoBind
    removeArea(area: XGridArea) {
        this.areas = this.areas.filter((a) => a.id !== area.id);
    }

    @AutoBind
    addRow(row: XGridRow, index?: number) {
        if (this.getFormContext()) {
            row.setFormContext(this.getFormContext());
        }

        if (index !== undefined) {
            this.rows.splice(index, 1, row);
        } else {
            this.rows.push(row);
        }
        this.rowsLength = this.rows.length;
    }

    @AutoBind
    removeRow(row: XGridRow) {
        this.rows = this.rows.filter((r) => r.id !== row.id);
    }

    render() {
        let sortedAreas = this.areas.sort((a, b) => {
            if (a.row === b.row) {
                return a.column - b.column;
            }
            return a.row - b.row;
        });

        let mode = this.getFormContext()?.mode ?? FormMode.Runtime;

        const rowsTemplate = this.rows
            .map((row) => (row.height ? `${row.height}px` : `minmax(75px, max-content)`))
            .join(" ");

        if (mode !== FormMode.LayoutDesign) {
            return (
                <GridContainerRender
                    padding={this.getInnerMargin()}
                    hasBorder={this.hasBorder()}
                    borderColor={this.borderColor()}
                    borderStyle={this.borderStyle()}
                    borderWidth={this.borderWidth()}
                    borderRadius={this.borderRadius()}
                    backgroundColor={this.backgroundColor()}
                    rowsTemplate={rowsTemplate}
                >
                    {sortedAreas.map((area) => {
                        return <React.Fragment key={area.id}>{area.render()}</React.Fragment>;
                    })}
                </GridContainerRender>
            );
        }

        return (
            <GridContainerRender
                padding={this.getInnerMargin()}
                hasBorder={this.hasBorder()}
                borderColor={this.borderColor()}
                borderStyle={this.borderStyle()}
                borderWidth={this.borderWidth()}
                borderRadius={this.borderRadius()}
                backgroundColor={this.backgroundColor()}
                rowsTemplate={rowsTemplate}
            >
                {sortedAreas.map((area) => {
                    return <React.Fragment key={area.id}>{area.render()}</React.Fragment>;
                })}
                {sortedAreas.length === 0 && <PlaceholderGrid>Grid Layout Not Configured</PlaceholderGrid>}
            </GridContainerRender>
        );
    }

    getInnerMargin(): number | undefined {
        return this.innerMargin || this.getFormContext()?.form?.defaultInnerContainerMargin;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "rowsLength",
            "columnsLength",
            "innerMargin",
            "gapBetweenAreas",
            "defaultAreaInnerMargin",
            "defaultShowAreaBorder",
            "defaultAreaBorderColor",
            "defaultAreaBorderRadius",
            "defaultAreaBorderWidth",
            "defaultAreaBorderStyle",
            "defaultAreaBackgroundColor",
        );

        Reflect.set(
            retData,
            "__areas",
            this.areas.map((c) => {
                return c.getStorageData();
            }),
        );

        Reflect.set(
            retData,
            "__rows",
            this.rows.map((c) => {
                return c.getStorageData();
            }),
        );
        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);
        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "rowsLength",
            "columnsLength",
            "innerMargin",
            "gapBetweenAreas",
            "defaultAreaInnerMargin",
            "defaultShowAreaBorder",
            "defaultAreaBorderColor",
            "defaultAreaBorderRadius",
            "defaultAreaBorderWidth",
            "defaultAreaBorderStyle",
            "defaultAreaBackgroundColor",
        );
        let rows = Reflect.get(data, "__rows");

        if (!rows) {
            throw new Error("Grid Continer: rows is required");
        }

        if (Array.isArray(rows)) {
        }

        this.rows = rows.map((r: object) => {
            let row = new XGridRow();
            row.setStorageData(r);
            return row;
        });

        let areas = Reflect.get(data, "__areas");

        this.areas = areas.map((a: object) => {
            let area = new XGridArea(0, 0, 1, 1);
            area.setStorageData(a);

            return area;
        });
    }
}

export const GridContainerRender = styled.div<{
    padding?: number;
    hasBorder?: boolean;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    borderStyle?: BorderStyle;
    backgroundColor?: string | null;
    gapBetweenAreas?: number;
    rowsTemplate: string;
}>`
    min-height: ${(props) => (props.padding || 0) + 75}px;
    width: 100%;

    display: grid;

    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: ${(props) => props.rowsTemplate};

    padding: ${(props) => props.padding || 0}px;
    border: ${(props) =>
        props.hasBorder
            ? `${(props.borderStyle || FormDesignConstants.CONTAINER_BORDER_STYLE).toLowerCase()} ${
                  props.borderWidth || FormDesignConstants.CONTAINER_BORDER_COLOR
              }px ${props.borderColor || FormDesignConstants.CONTAINER_BORDER_COLOR}`
            : "none"};
    border-radius: ${(props) => props.borderRadius || FormDesignConstants.CONTAINER_BORDER_RADIOUS}px;

    background-color: ${(props) => props.backgroundColor || FormDesignConstants.CONTAINER_BACKGROUND_COLOR};
    position: relative;

    column-gap: ${(props) => props.gapBetweenAreas || 5}px;
    row-gap: ${(props) => props.gapBetweenAreas || 5}px;
`;

export const GridCellRender = styled.div<{
    padding?: number;
    hasBorder?: boolean;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    borderStyle?: BorderStyle;
    backgroundColor?: string | null;
    height?: number;
    rowSpan?: number;
    rowStart: number;
    columnSpan: number;
    columnStart: number;
}>`
    display: flex;
    flex-direction: column;
    position: relative;
    padding: ${(props) => props.padding || 0}px;
    min-height: 72px;
    height: 100%;
    overflow: auto;

    ${(props) => (props.height !== undefined ? `max-height: ${props.height}px; overflow: auto;` : ``)}

    border: ${(props) => `${props.borderStyle} ${props.borderWidth}px ${props.borderColor}`};
    border-radius: ${(props) => props.borderRadius}px;
    background-color: ${(props) => props.backgroundColor || FormDesignConstants.CONTAINER_BACKGROUND_COLOR};

    grid-column: ${(props) => props.columnStart + 1} / span ${(props) => props.columnSpan};
    grid-row-start: ${(props) => props.rowStart + 1};
    grid-row-end: ${(props) => (props.rowSpan !== undefined ? props.rowStart + 1 + props.rowSpan : props.rowStart + 2)};
`;

const OneControlRender = styled.div<{ position: { top?: number; bottom?: number; left?: number; right?: number } }>`
    position: absolute;

    ${(props) =>
        props.position.top !== undefined ||
        props.position.left !== undefined ||
        props.position.bottom !== undefined ||
        props.position.right !== undefined
            ? `
                top: ${props.position.top || 0}px;
                left: ${props.position.left || 0}px;
                right: ${props.position.right || 0}px;
                bottom: ${props.position.bottom || 0}px;
            `
            : `
                top: 50%;
                left: 50%;
                transform: translateY(-50%) translateX(-50%);

            `}
`;

const PlaceholderGrid = styled.span`
    grid-column: 1 / span 12;
    grid-row: 1 / span 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed ${UIStyleConstants.GRAY_COLOR};
    border-radius: 5px;
`;
